"use client";

import { PropsWithChildren, createContext, useRef } from "react";
import {
	GoogleAuthClient,
	AuthProvider as Provider,
	createAuthClient,
} from "..";
import { OutlookAuthClient } from "../lib/providers/outlook/mod";
import { GoogleOneTap } from "./providers/google";

export const Context = createContext<Pick<
	GoogleAuthClient | OutlookAuthClient,
	"signIn" | "signOut" | "on"
> | null>(null);

const { Provider: ContextProvider } = Context;

interface AuthProviderProps {
	provider: Provider;
	appUrl: string;
	fallbackRedirectUrl: string;
}

/**
 * Component that provides authentication context to its children
 * @param props - The component props
 * @param props.provider - The authentication provider to use e.g. Google, Outlook, etc.
 * @param props.appUrl - The base URL of the oauth route
 * @param props.fallbackRedirectUrl - The URL used as a fallback when not specified in `signIn`
 * @param props.children - Child components that will have access to auth context
 */
export function AuthProvider({
	provider,
	appUrl,
	fallbackRedirectUrl,
	children,
}: PropsWithChildren<AuthProviderProps>) {
	const client = useRef(
		createAuthClient({
			appUrl,
			provider,
			fallbackRedirectUrl,
		}),
	);

	const { signIn, signOut, on } = client.current;

	return (
		<ContextProvider value={{ signIn, signOut, on }}>
			{children}

			{/* This is a quick implementation for now and we might want to change this in the future
					once we add more providers	*/}
			{client.current.name === "google" && client.current.withOneTap() ? (
				<GoogleOneTap redirectUrl={fallbackRedirectUrl} />
			) : null}
		</ContextProvider>
	);
}
